/**
 * Store Locations JavaScript
 * Handles city navigation, slider functionality, and autoplay
 */

class StoreLocations {
  constructor() {
    this.sections = document.querySelectorAll('.store-locations-section');
    this.init();
  }

  init() {
    this.sections.forEach(section => {
      new StoreLocationsSection(section);
    });
  }
}

class StoreLocationsSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.citiesSlider = section.querySelector('[data-cities-slider]');
    this.cityTabs = section.querySelectorAll('.city-tab');
    this.cityContents = section.querySelectorAll('.city-content');
    this.prevButton = section.querySelector('.nav-prev');
    this.nextButton = section.querySelector('.nav-next');
    this.currentIndex = 0;
    this.autoplayInterval = null;
    this.isAnimating = false;

    this.init();
  }

  init() {
    if (!this.citiesSlider || this.cityTabs.length === 0) return;

    this.setupEventListeners();
    this.updateNavigationButtons();
    this.setupAutoplay();
    this.setupResponsiveBehavior();
  }

  setupEventListeners() {
    // City tab clicks
    this.cityTabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        this.switchToCity(index);
      });
    });

    // Navigation arrows
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.previousCity();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.nextCity();
      });
    }

    // Keyboard navigation
    this.section.addEventListener('keydown', (e) => {
      this.handleKeyboardNavigation(e);
    });

    // Touch/swipe support for mobile
    this.setupTouchSupport();

    // Pause autoplay on hover
    this.section.addEventListener('mouseenter', () => {
      this.pauseAutoplay();
    });

    this.section.addEventListener('mouseleave', () => {
      this.resumeAutoplay();
    });

    // Pause autoplay when tab is not visible
    this.setupVisibilityListener();
  }

  switchToCity(index) {
    if (this.isAnimating || index === this.currentIndex) return;

    this.isAnimating = true;
    const previousIndex = this.currentIndex;
    this.currentIndex = index;

    // Update active tab
    this.cityTabs[previousIndex].classList.remove('active');
    this.cityTabs[this.currentIndex].classList.add('active');

    // Update active content
    this.cityContents[previousIndex].classList.remove('active');
    this.cityContents[this.currentIndex].classList.add('active');

    // Update slider position
    this.updateSliderPosition();

    // Update navigation buttons
    this.updateNavigationButtons();

    // Reset autoplay timer
    this.resetAutoplay();

    // Animation complete
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  previousCity() {
    const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.cityTabs.length - 1;
    this.switchToCity(newIndex);
  }

  nextCity() {
    const newIndex = this.currentIndex < this.cityTabs.length - 1 ? this.currentIndex + 1 : 0;
    this.switchToCity(newIndex);
  }

  updateSliderPosition() {
    if (!this.citiesSlider) return;

    const activeTab = this.cityTabs[this.currentIndex];
    const sliderWidth = this.citiesSlider.parentElement.offsetWidth;
    const tabWidth = activeTab.offsetWidth;
    const tabLeft = activeTab.offsetLeft;
    const centerPosition = tabLeft - (sliderWidth / 2) + (tabWidth / 2);

    // Ensure we don't scroll past the beginning
    const maxScroll = this.citiesSlider.scrollWidth - sliderWidth;
    const scrollPosition = Math.max(0, Math.min(centerPosition, maxScroll));

    this.citiesSlider.style.transform = `translateX(-${scrollPosition}px)`;
  }

  updateNavigationButtons() {
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex === this.cityTabs.length - 1;
    }
  }

  handleKeyboardNavigation(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.previousCity();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.nextCity();
        break;
      case 'Home':
        e.preventDefault();
        this.switchToCity(0);
        break;
      case 'End':
        e.preventDefault();
        this.switchToCity(this.cityTabs.length - 1);
        break;
    }
  }

  setupTouchSupport() {
    let startX = 0;
    let startY = 0;
    let isDragging = false;

    this.section.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = false;
    });

    this.section.addEventListener('touchmove', (e) => {
      if (!startX || !startY) return;

      const deltaX = e.touches[0].clientX - startX;
      const deltaY = e.touches[0].clientY - startY;

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        isDragging = true;
        e.preventDefault();
      }
    });

    this.section.addEventListener('touchend', (e) => {
      if (!isDragging) return;

      const deltaX = e.changedTouches[0].clientX - startX;
      const threshold = 50;

      if (Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          this.previousCity();
        } else {
          this.nextCity();
        }
      }

      startX = 0;
      startY = 0;
      isDragging = false;
    });
  }

  setupAutoplay() {
    const autoplayEnabled = this.section.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(this.section.dataset.autoplaySpeed) || 5000;

    if (autoplayEnabled && this.cityTabs.length > 1) {
      this.autoplaySpeed = autoplaySpeed;
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.autoplayInterval = setInterval(() => {
      this.nextCity();
    }, this.autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  resumeAutoplay() {
    if (this.autoplaySpeed && !this.autoplayInterval) {
      this.startAutoplay();
    }
  }

  resetAutoplay() {
    this.pauseAutoplay();
    this.resumeAutoplay();
  }

  setupVisibilityListener() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.resumeAutoplay();
        } else {
          this.pauseAutoplay();
        }
      });
    }, { threshold: 0.1 });

    observer.observe(this.section);
  }

  setupResponsiveBehavior() {
    const updateSliderOnResize = () => {
      this.updateSliderPosition();
    };

    window.addEventListener('resize', debounce(updateSliderOnResize, 250));
  }
}

// Utility function for debouncing
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new StoreLocations();
});

// Initialize for dynamic content (Shopify sections)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.classList.contains('store-locations-section')) {
    new StoreLocationsSection(event.target);
  }
});

// Clean up when section is unloaded
document.addEventListener('shopify:section:unload', (event) => {
  if (event.target.classList.contains('store-locations-section')) {
    // Clean up any intervals or event listeners if needed
    const section = event.target;
    const autoplayInterval = section._autoplayInterval;
    if (autoplayInterval) {
      clearInterval(autoplayInterval);
    }
  }
});

// Export for potential use in other scripts
window.StoreLocations = StoreLocations;
window.StoreLocationsSection = StoreLocationsSection;
